import React, { useEffect, useMemo } from 'react';
import { Card, CardContent } from './ui/Card';
import { Input, Label } from './ui/Forms';
import { InfoTooltip } from './ui/InfoTooltip';
import { formatCurrency, futureDate } from '../utils/currency';
import { Currency, LiquidityEvent } from '../types';
import { Flame, AlertTriangle, TrendingUp, ArrowDown, CheckCircle2, Infinity as InfinityIcon, Lock, Unlock, Building2 } from 'lucide-react';
import { findEarliestRetirement, getPortfolioAtYear, simulateDrawdownBreakdown, DrawdownYearBreakdown, IlliquidBucket } from '../utils/projection';
function getAgeAtYear(dob: string, yearsFromNow: number): number | null {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return null;
  const now = new Date();
  const currentAge = now.getFullYear() - birthDate.getFullYear();
  const hadBirthdayThisYear = now.getMonth() > birthDate.getMonth() || now.getMonth() === birthDate.getMonth() && now.getDate() >= birthDate.getDate();
  return (hadBirthdayThisYear ? currentAge : currentAge - 1) + yearsFromNow;
}
function AgeChips({
  partnerNames,
  partnerDOBs,
  yearsFromNow,
  className = ''
}: {
  partnerNames: [string, string];
  partnerDOBs: [string, string];
  yearsFromNow: number;
  className?: string;
}) {
  const dobs = partnerDOBs || ['', ''];
  const age1 = getAgeAtYear(dobs[0], yearsFromNow);
  const age2 = getAgeAtYear(dobs[1], yearsFromNow);
  if (age1 === null && age2 === null) return null;
  return <span className={`inline-flex items-center gap-1.5 text-xs ${className}`} data-id="element-227">
      {age1 !== null && <span data-id="element-228">
          {partnerNames[0]}: {age1}
        </span>}
      {age1 !== null && age2 !== null && <span data-id="element-229">·</span>}
      {age2 !== null && <span data-id="element-230">
          {partnerNames[1]}: {age2}
        </span>}
    </span>;
}
interface FireWidgetProps {
  baseCurrency: Currency;
  totalLiquid: number;
  totalIlliquid: number;
  liquidReturnRate: number;
  illiquidReturnRate: number;
  drawdownRate: number;
  monthlyContribution: number;
  timeHorizon: number;
  withdrawalStrategy: 'drawdown' | 'interest-only';
  onRetirementYearChange: (year: number | null) => void;
  onDrawdownChange: (amount: number) => void;
  desiredDrawdown: number;
  partnerNames: [string, string];
  partnerDOBs: [string, string];
  liquidityEvents?: LiquidityEvent[];
  annualInflation?: number;
  capitalGainsTaxRate?: number;
  illiquidBuckets?: IlliquidBucket[];
}
export function FireWidget({
  baseCurrency,
  totalLiquid,
  totalIlliquid,
  liquidReturnRate,
  illiquidReturnRate,
  drawdownRate,
  monthlyContribution,
  timeHorizon,
  withdrawalStrategy,
  onRetirementYearChange,
  onDrawdownChange,
  desiredDrawdown,
  partnerNames,
  partnerDOBs,
  liquidityEvents = [],
  annualInflation = 0,
  capitalGainsTaxRate = 0,
  illiquidBuckets
}: FireWidgetProps) {
  const totalStartingBalance = totalLiquid + totalIlliquid;
  // Blended accumulation rate for display
  const blendedRate = useMemo(() => {
    if (totalStartingBalance === 0) return (liquidReturnRate + illiquidReturnRate) / 2;
    return (totalLiquid * liquidReturnRate + totalIlliquid * illiquidReturnRate) / totalStartingBalance;
  }, [totalLiquid, totalIlliquid, totalStartingBalance, liquidReturnRate, illiquidReturnRate]);
  // Find earliest sustainable retirement year
  const result = useMemo(() => {
    if (desiredDrawdown <= 0) return null;
    return findEarliestRetirement(totalLiquid, totalIlliquid, liquidReturnRate, illiquidReturnRate, drawdownRate, monthlyContribution, desiredDrawdown, timeHorizon, withdrawalStrategy, liquidityEvents, baseCurrency, annualInflation, capitalGainsTaxRate, illiquidBuckets);
  }, [totalLiquid, totalIlliquid, liquidReturnRate, illiquidReturnRate, drawdownRate, monthlyContribution, desiredDrawdown, timeHorizon, withdrawalStrategy, liquidityEvents, baseCurrency, annualInflation, capitalGainsTaxRate, illiquidBuckets]);
  const earliestYear = result?.year ?? -1;
  const portfolioAtRetirement = result?.portfolioAtRetirement ?? 0;
  // Year-by-year portfolio milestones
  const milestones = useMemo(() => {
    const points: {
      year: number;
      portfolio: number;
      canRetire: boolean;
    }[] = [];
    const checkYears = [5, 10, 15, 20, 25, 30, 40, 50].filter(y => y <= timeHorizon);
    for (const y of checkYears) {
      const portfolio = getPortfolioAtYear(totalLiquid, totalIlliquid, liquidReturnRate, illiquidReturnRate, monthlyContribution, y, liquidityEvents, baseCurrency, capitalGainsTaxRate);
      points.push({
        year: y,
        portfolio,
        canRetire: earliestYear >= 0 && y >= earliestYear
      });
    }
    return points;
  }, [totalLiquid, totalIlliquid, liquidReturnRate, illiquidReturnRate, monthlyContribution, timeHorizon, earliestYear, liquidityEvents, baseCurrency]);
  // Max portfolio for scaling bars
  const maxPortfolio = useMemo(() => {
    return Math.max(...milestones.map(m => m.portfolio), 1);
  }, [milestones]);
  // Drawdown funding source breakdown
  const drawdownBreakdown = useMemo(() => {
    if (earliestYear < 0 || desiredDrawdown <= 0) return [];
    return simulateDrawdownBreakdown(totalLiquid, totalIlliquid, liquidReturnRate, illiquidReturnRate, drawdownRate, monthlyContribution, desiredDrawdown, earliestYear, timeHorizon, withdrawalStrategy, liquidityEvents, baseCurrency, annualInflation, capitalGainsTaxRate);
  }, [totalLiquid, totalIlliquid, liquidReturnRate, illiquidReturnRate, drawdownRate, monthlyContribution, desiredDrawdown, earliestYear, timeHorizon, withdrawalStrategy, liquidityEvents, baseCurrency, annualInflation, capitalGainsTaxRate]);
  // Push to parent
  useEffect(() => {
    onRetirementYearChange(earliestYear >= 0 ? earliestYear : null);
  }, [earliestYear]);
  useEffect(() => {
    onDrawdownChange(desiredDrawdown);
  }, [desiredDrawdown]);
  const fireTarget = useMemo(() => {
    if (withdrawalStrategy === 'interest-only' && drawdownRate > 0) {
      return desiredDrawdown * 12 / (drawdownRate / 100);
    }
    return null;
  }, [desiredDrawdown, drawdownRate, withdrawalStrategy]);
  return <Card className="overflow-hidden" data-id="element-231">
      {/* Header */}
      <div className="bg-slate-50 border-b border-slate-200 px-6 py-5 flex justify-between items-center flex-wrap gap-3" data-id="element-232">
        <div className="flex items-center gap-3" data-id="element-233">
          <div className="bg-amber-500 p-2 rounded-lg shadow-sm" data-id="element-234">
            <Flame className="w-5 h-5 text-white" data-id="element-235" />
          </div>
          <div data-id="element-236">
            <h3 className="text-lg font-bold text-slate-800" data-id="element-237">FIRE Planning</h3>
            <p className="text-sm text-slate-500" data-id="element-238">When can you retire?</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap" data-id="element-239">
          <span className="bg-slate-100 px-2.5 py-1 rounded-full" data-id="element-240">
            Growth:{' '}
            <span className="font-semibold text-slate-600" data-id="element-241">
              {blendedRate.toFixed(1)}%
            </span>
          </span>
          <span className={`px-2.5 py-1 rounded-full ${withdrawalStrategy === 'interest-only' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-242">
            <span className="font-semibold" data-id="element-243">
              {withdrawalStrategy === 'interest-only' ? 'Interest Only' : 'Drawdown'}
            </span>
          </span>
          {annualInflation > 0 && <span className="bg-rose-50 px-2.5 py-1 rounded-full text-rose-700" data-id="element-244">
              Inflation:{' '}
              <span className="font-semibold" data-id="element-245">{annualInflation}%</span>
            </span>}
          {capitalGainsTaxRate > 0 && <span className="bg-slate-100 px-2.5 py-1 rounded-full text-slate-600" data-id="element-246">
              Tax: <span className="font-semibold" data-id="element-247">{capitalGainsTaxRate}%</span>
            </span>}
        </div>
      </div>

      <CardContent className="p-6 space-y-6" data-id="element-248">
        {/* Main result */}
        <div className="bg-slate-800 rounded-2xl p-6 text-white shadow-md relative overflow-hidden" data-id="element-249">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 opacity-5" data-id="element-250">
            <Flame className="w-48 h-48" data-id="element-251" />
          </div>
          <div className="relative z-10" data-id="element-252">
            {desiredDrawdown <= 0 ? <div className="text-center py-4" data-id="element-253">
                <p className="text-slate-400" data-id="element-254">
                  Set a desired monthly income in Planning Assumptions above to
                  see when retirement is sustainable.
                </p>
              </div> : earliestYear >= 0 ? <>
                <p className="text-slate-400 font-medium mb-2 uppercase tracking-wider text-xs" data-id="element-255">
                  {earliestYear === 0 ? 'You can retire' : 'You could retire in'}
                </p>
                {earliestYear === 0 ? <h2 className="text-4xl font-bold text-emerald-400 mb-1" data-id="element-256">
                    🎉 Goal achieved!
                  </h2> : <h2 className="text-4xl font-bold text-amber-400 mb-1" data-id="element-257">
                    {earliestYear} {earliestYear === 1 ? 'year' : 'years'} from
                    now
                  </h2>}
                <div className="flex items-center gap-3 mb-4" data-id="element-258">
                  <span className="text-slate-500 text-sm" data-id="element-259">
                    {earliestYear === 0 ? 'Your portfolio already supports this income' : futureDate(earliestYear)}
                  </span>
                  <AgeChips partnerNames={partnerNames} partnerDOBs={partnerDOBs} yearsFromNow={earliestYear} className="text-slate-400" data-id="element-260" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-4 border-t border-slate-700" data-id="element-261">
                  <div data-id="element-262">
                    <p className="text-slate-400 text-xs mb-1" data-id="element-263">
                      {withdrawalStrategy === 'interest-only' ? 'Target FIRE Number' : 'Net worth at retirement'}
                    </p>
                    <p className="text-xl font-bold" data-id="element-264">
                      {formatCurrency(withdrawalStrategy === 'interest-only' && fireTarget ? fireTarget : portfolioAtRetirement, baseCurrency)}
                    </p>
                  </div>
                  <div data-id="element-265">
                    <p className="text-slate-400 text-xs mb-1" data-id="element-266">
                      Monthly income
                    </p>
                    <p className="text-xl font-bold" data-id="element-267">
                      {formatCurrency(desiredDrawdown, baseCurrency)}
                    </p>
                    <p className="text-slate-500 text-xs mt-0.5" data-id="element-268">
                      = {formatCurrency(desiredDrawdown * 12, baseCurrency)}/yr
                    </p>
                  </div>
                  <div data-id="element-269">
                    <p className="text-slate-400 text-xs mb-1" data-id="element-270">
                      {withdrawalStrategy === 'interest-only' ? 'Portfolio longevity' : 'Drawdown period'}
                    </p>
                    <p className="text-xl font-bold flex items-center gap-1" data-id="element-271">
                      {withdrawalStrategy === 'interest-only' ? <>
                          <InfinityIcon className="w-5 h-5" data-id="element-272" /> Indefinite
                        </> : `${timeHorizon - earliestYear} years`}
                    </p>
                    {withdrawalStrategy === 'drawdown' && <AgeChips partnerNames={partnerNames} partnerDOBs={partnerDOBs} yearsFromNow={timeHorizon} className="text-slate-500 mt-0.5" data-id="element-273" />}
                  </div>
                  <div data-id="element-274">
                    <p className="text-slate-400 text-xs mb-1" data-id="element-275">
                      Starting balance
                    </p>
                    <p className="text-xl font-bold" data-id="element-276">
                      {formatCurrency(totalStartingBalance, baseCurrency)}
                    </p>
                    <p className="text-slate-500 text-xs mt-0.5" data-id="element-277">
                      + {formatCurrency(monthlyContribution, baseCurrency)}/mo
                    </p>
                  </div>
                </div>
              </> : <div className="flex items-start gap-3 py-2" data-id="element-278">
                <AlertTriangle className="w-7 h-7 text-amber-400 shrink-0 mt-0.5" data-id="element-279" />
                <div data-id="element-280">
                  <h2 className="text-xl font-bold mb-1" data-id="element-281">
                    Not achievable within {timeHorizon} years
                  </h2>
                  <p className="text-slate-400 text-sm" data-id="element-282">
                    Your projected net worth can't sustain{' '}
                    {formatCurrency(desiredDrawdown, baseCurrency)}/mo
                    withdrawals. Try increasing contributions, return rates,
                    time horizon, or reducing the drawdown amount.
                  </p>
                </div>
              </div>}
          </div>
        </div>

        {/* Illiquid Asset Availability Timeline */}
        {illiquidBuckets && illiquidBuckets.length > 0 && desiredDrawdown > 0 && <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-id="element-283">
              <div className="mb-4" data-id="element-284">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2" data-id="element-285">
                  <Building2 className="w-4 h-4 text-indigo-500" data-id="element-286" />
                  Illiquid Asset Availability
                </h4>
                <p className="text-xs text-slate-400 mt-0.5" data-id="element-287">
                  When each illiquid asset becomes available for drawdown and
                  its projected impact
                </p>
              </div>

              <div className="space-y-2" data-id="element-288">
                {illiquidBuckets.filter(b => b.availableYear < 999).sort((a, b) => a.availableYear - b.availableYear).map((bucket, idx) => {
            // Project the bucket's value at its availability year
            const monthlyRate = bucket.returnRate / 100 / 12;
            const taxMul = 1 - (capitalGainsTaxRate ?? 0) / 100;
            let projectedValue = bucket.amount;
            for (let y = 0; y < bucket.availableYear; y++) {
              for (let m = 0; m < 12; m++) {
                projectedValue += projectedValue * monthlyRate * taxMul;
              }
            }
            const monthsCovered = desiredDrawdown > 0 ? Math.floor(projectedValue / desiredDrawdown) : 0;
            const isAvailableNow = bucket.availableYear === 0;
            const isAvailableAtRetirement = earliestYear >= 0 && bucket.availableYear <= earliestYear;
            return <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg border ${isAvailableNow ? 'bg-emerald-50/50 border-emerald-100' : isAvailableAtRetirement ? 'bg-blue-50/50 border-blue-100' : 'bg-amber-50/50 border-amber-100'}`} data-id="element-289">
                        <div className={`p-1.5 rounded-md ${isAvailableNow ? 'bg-emerald-100 text-emerald-600' : isAvailableAtRetirement ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-290">
                          {isAvailableNow || isAvailableAtRetirement ? <Unlock className="w-3.5 h-3.5" data-id="element-291" /> : <Lock className="w-3.5 h-3.5" data-id="element-292" />}
                        </div>

                        <div className="flex-1 min-w-0" data-id="element-293">
                          <div className="flex items-center gap-2" data-id="element-294">
                            <span className="text-sm font-medium text-slate-800 truncate" data-id="element-295">
                              {bucket.name}
                            </span>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${bucket.yieldsAfterDrawdown ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-296">
                              {bucket.yieldsAfterDrawdown ? 'Continues yielding' : 'Sells at drawdown'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5" data-id="element-297">
                            <span className="text-xs text-slate-500" data-id="element-298">
                              {isAvailableNow ? 'Available now' : `Yr ${bucket.availableYear} · ${futureDate(bucket.availableYear)}`}
                            </span>
                            {bucket.returnRate > 0 && <span className="text-xs text-slate-400" data-id="element-299">
                                {bucket.returnRate.toFixed(1)}% return
                              </span>}
                          </div>
                        </div>

                        <div className="text-right shrink-0" data-id="element-300">
                          <p className="text-sm font-semibold text-slate-800" data-id="element-301">
                            {formatCurrency(Math.round(projectedValue), baseCurrency)}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5" data-id="element-302">
                            {isAvailableNow ? 'current' : 'projected'} · covers{' '}
                            <span className="font-semibold text-slate-600" data-id="element-303">
                              {monthsCovered} mo
                            </span>{' '}
                            of drawdown
                          </p>
                        </div>
                      </div>;
          })}

                {/* Assets held indefinitely */}
                {illiquidBuckets.filter(b => b.availableYear >= 999).length > 0 && <div className="mt-2 pt-2 border-t border-slate-100" data-id="element-304">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5" data-id="element-305">
                      Held indefinitely (not available for drawdown)
                    </p>
                    {illiquidBuckets.filter(b => b.availableYear >= 999).map((bucket, idx) => <div key={`hold-${idx}`} className="flex items-center gap-3 p-2 rounded-lg" data-id="element-306">
                          <div className="p-1.5 rounded-md bg-slate-100 text-slate-400" data-id="element-307">
                            <Lock className="w-3.5 h-3.5" data-id="element-308" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-309">
                            <span className="text-sm font-medium text-slate-500 truncate block" data-id="element-310">
                              {bucket.name}
                            </span>
                            <span className="text-xs text-slate-400" data-id="element-311">
                              Hold strategy · {bucket.returnRate.toFixed(1)}%
                              appreciation
                            </span>
                          </div>
                          <div className="text-right shrink-0" data-id="element-312">
                            <p className="text-sm font-medium text-slate-500" data-id="element-313">
                              {formatCurrency(Math.round(bucket.amount), baseCurrency)}
                            </p>
                            <p className="text-[10px] text-slate-400" data-id="element-314">
                              current value
                            </p>
                          </div>
                        </div>)}
                  </div>}
              </div>
            </div>}

        {/* Net worth growth timeline — full width */}
        {desiredDrawdown > 0 && milestones.length > 0 && <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-id="element-315">
            <h4 className="text-sm font-semibold text-slate-700 mb-4" data-id="element-316">
              Projected Net Worth Over Time
            </h4>
            <div className="space-y-2" data-id="element-317">
              {milestones.map(m => <div key={m.year} className="flex items-center gap-3" data-id="element-318">
                  <div className="text-right shrink-0 w-24" data-id="element-319">
                    <span className="text-xs font-medium text-slate-400 block" data-id="element-320">
                      Yr {m.year}
                    </span>
                    <span className="text-[10px] text-slate-300 block" data-id="element-321">
                      {futureDate(m.year)}
                    </span>
                    <AgeChips partnerNames={partnerNames} partnerDOBs={partnerDOBs} yearsFromNow={m.year} className="text-slate-300 text-[10px]" data-id="element-322" />
                  </div>
                  <div className="flex-1 relative" data-id="element-323">
                    <div className="h-7 bg-slate-50 rounded-md overflow-hidden border border-slate-100 relative" data-id="element-324">
                      <div className={`h-full rounded-md transition-all duration-300 ${m.canRetire ? 'bg-emerald-100' : 'bg-blue-50'}`} style={{
                  width: `${Math.min(m.portfolio / maxPortfolio * 100, 100)}%`
                }} data-id="element-325" />
                      <div className="absolute inset-0 flex items-center px-3 justify-between" data-id="element-326">
                        <span className="text-xs font-medium text-slate-700" data-id="element-327">
                          {formatCurrency(m.portfolio, baseCurrency)}
                        </span>
                        {m.canRetire && <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1" data-id="element-328">
                            <CheckCircle2 className="w-3 h-3" data-id="element-329" />
                            Sustainable
                          </span>}
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}

        {/* Drawdown Funding Sources — full width */}
        {earliestYear >= 0 && desiredDrawdown > 0 && drawdownBreakdown.length > 0 && <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-id="element-330">
              <div className="mb-4" data-id="element-331">
                <h4 className="text-sm font-semibold text-slate-700" data-id="element-332">
                  Drawdown Funding Sources
                </h4>
                <p className="text-xs text-slate-400 mt-0.5" data-id="element-333">
                  Where your annual withdrawal comes from during retirement
                </p>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 mb-4 text-xs" data-id="element-334">
                <div className="flex items-center gap-1.5" data-id="element-335">
                  <div className="w-3 h-3 rounded-sm bg-emerald-400" data-id="element-336" />
                  <span className="text-slate-600" data-id="element-337">Interest</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-338">
                  <div className="w-3 h-3 rounded-sm bg-violet-400" data-id="element-339" />
                  <span className="text-slate-600" data-id="element-340">Cash Events</span>
                </div>
                {withdrawalStrategy === 'drawdown' && <div className="flex items-center gap-1.5" data-id="element-341">
                    <div className="w-3 h-3 rounded-sm bg-amber-400" data-id="element-342" />
                    <span className="text-slate-600" data-id="element-343">Principal</span>
                  </div>}
              </div>

              <div className="space-y-1.5" data-id="element-344">
                {drawdownBreakdown.map(row => {
            const total = row.totalWithdrawal || 1;
            const interestPct = row.fromInterest / total * 100;
            const eventsPct = row.fromEvents / total * 100;
            const principalPct = row.fromPrincipal / total * 100;
            return <div key={row.year} className="flex items-center gap-3" data-id="element-345">
                      <div className="text-right shrink-0 w-24" data-id="element-346">
                        <span className="text-xs font-medium text-slate-400 block" data-id="element-347">
                          Yr {row.year}
                        </span>
                        <span className="text-[10px] text-slate-300 block" data-id="element-348">
                          {futureDate(row.year)}
                        </span>
                        <AgeChips partnerNames={partnerNames} partnerDOBs={partnerDOBs} yearsFromNow={row.year} className="text-slate-300 text-[10px]" data-id="element-349" />
                      </div>
                      <div className="flex-1" data-id="element-350">
                        <div className="h-6 rounded-md overflow-hidden flex bg-slate-100 border border-slate-100" data-id="element-351">
                          {interestPct > 0 && <div className="bg-emerald-400 h-full transition-all duration-300 relative group" style={{
                    width: `${interestPct}%`
                  }} title={`Interest: ${formatCurrency(row.fromInterest, baseCurrency)}`} data-id="element-352" />}
                          {eventsPct > 0 && <div className="bg-violet-400 h-full transition-all duration-300 relative group" style={{
                    width: `${eventsPct}%`
                  }} title={`Events: ${formatCurrency(row.fromEvents, baseCurrency)}`} data-id="element-353" />}
                          {principalPct > 0 && <div className="bg-amber-400 h-full transition-all duration-300 relative group" style={{
                    width: `${principalPct}%`
                  }} title={`Principal: ${formatCurrency(row.fromPrincipal, baseCurrency)}`} data-id="element-354" />}
                        </div>
                      </div>
                      <div className="shrink-0 w-24 text-right" data-id="element-355">
                        <span className="text-xs text-slate-500" data-id="element-356">
                          {formatCurrency(row.totalWithdrawal, baseCurrency)}
                        </span>
                      </div>
                    </div>;
          })}
              </div>

              {/* Summary row */}
              {drawdownBreakdown.length > 0 && <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-3 gap-4 text-center" data-id="element-357">
                  <div data-id="element-358">
                    <p className="text-xs text-slate-400 mb-0.5" data-id="element-359">
                      Total from Interest
                    </p>
                    <p className="text-sm font-semibold text-emerald-600" data-id="element-360">
                      {formatCurrency(drawdownBreakdown.reduce((s, r) => s + r.fromInterest, 0), baseCurrency)}
                    </p>
                  </div>
                  <div data-id="element-361">
                    <p className="text-xs text-slate-400 mb-0.5" data-id="element-362">
                      Total from Events
                    </p>
                    <p className="text-sm font-semibold text-violet-600" data-id="element-363">
                      {formatCurrency(drawdownBreakdown.reduce((s, r) => s + r.fromEvents, 0), baseCurrency)}
                    </p>
                  </div>
                  {withdrawalStrategy === 'drawdown' && <div data-id="element-364">
                      <p className="text-xs text-slate-400 mb-0.5" data-id="element-365">
                        Total from Principal
                      </p>
                      <p className="text-sm font-semibold text-amber-600" data-id="element-366">
                        {formatCurrency(drawdownBreakdown.reduce((s, r) => s + r.fromPrincipal, 0), baseCurrency)}
                      </p>
                    </div>}
                </div>}
            </div>}
      </CardContent>
    </Card>;
}